import { useCallback, useState } from 'react';

import { SendTestMessageMutation, useSendTestMessageMutation } from '@occc/graphql';
import { snackbar } from '@occc-app/design-system';
import { SendTestMessageInputSchema } from './send-test-message.schema';
import { TestMessageTemplateInput } from './send-test-message.types';
import { FetchResult } from '@apollo/client';

export type SendTestMessageControllerProps = TestMessageTemplateInput & {};

export type SendTestMessageControllerReturns = {
  membershipUuid: string;
  onMembershipUuidChange: (uuid: string) => void;
  canSendTestSms: boolean;
  canSendTestPush: boolean;
  sendTestMessage: (type?: 'sms' | 'push') => Promise<FetchResult<SendTestMessageMutation> | null>;
  loading: boolean;
  input: {
    membershipUuid: string;
    smsBody?: string | null;
    pushNotificationTemplate?: TestMessageTemplateInput['pushNotificationTemplate'] | null;
  };
};

export const useSendTestMessageController = (
  props: SendTestMessageControllerProps,
): SendTestMessageControllerReturns => {
  const { smsBody, pushNotificationTemplate } = props;

  const [sendTestMessageMutation, { loading }] = useSendTestMessageMutation();
  const [membershipUuid, setMembershipUuid] = useState('');

  const onError = useCallback(() => {
    snackbar.error(`Something went wrong. Please try again.`);
  }, []);

  const onCompleted = useCallback(
    (data: SendTestMessageMutation) => {
      if (data.sendTestMessage) {
        snackbar.success('Test message has been successfully sent');
      } else {
        onError();
      }
    },
    [onError],
  );

  const sendTestMessage = useCallback(
    async (type?: 'sms' | 'push') => {
      try {
        const input = SendTestMessageInputSchema.parse({
          membershipId: membershipUuid,
          messageTemplate: {
            smsBody: type && type !== 'sms' ? null : props.smsBody,
            pushNotificationTemplate:
              type && type !== 'push' ? null : props.pushNotificationTemplate,
          },
        });

        return sendTestMessageMutation({
          onError,
          onCompleted,
          variables: {
            input,
          },
        });
      } catch {
        onError?.();
        return null;
      }
    },
    [
      membershipUuid,
      onCompleted,
      onError,
      props.pushNotificationTemplate,
      props.smsBody,
      sendTestMessageMutation,
    ],
  );

  return {
    membershipUuid,
    onMembershipUuidChange: setMembershipUuid,
    canSendTestPush:
      !!membershipUuid &&
      !!pushNotificationTemplate?.notification.title &&
      !!pushNotificationTemplate.notification.body,
    canSendTestSms: !!membershipUuid && !!smsBody,
    sendTestMessage,
    loading,
    input: {
      membershipUuid,
      smsBody,
      pushNotificationTemplate,
    },
  };
};
